import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { getAuthLink } from '@/lib/links';
import { SectionBadge, FeatureCard, StatCard } from '@/components/marketing';
import {
  ArrowRight,
  BarChart3,
  Search,
  Globe,
  MapPin,
  AlertCircle,
  MessageSquare,
  Target,
  Megaphone,
} from 'lucide-react';

export const MarketingDashboardPage = () => {
  return (
    <>
      <SEOHead
        title="Marketing Command Center | Rooflyzer OS"
        description="Stop flying blind. Track real ROI, SEO performance, brand reputation, and lead sources in one marketing dashboard."
        canonicalUrl="/marketing-dashboard"
      />

      <div className="pt-24 min-h-screen px-4 sm:px-6 pb-20">
        {/* Hero */}
        <section className="text-center max-w-5xl mx-auto mb-20">
          <SectionBadge icon={Target} text="Marketing 360" color="pink" className="mb-6" />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Stop Paying for <span className="text-white">Vanity Metrics</span>
          </h1>
          <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
            Clicks don't pay the bills. Revenue does. Our Marketing Command Center connects your
            marketing efforts directly to closed deals, so you can finally hold your agency
            accountable.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 rounded-lg shadow-sm"
              onClick={() => window.location.assign(getAuthLink('/register'))}
            >
              Get Your Dashboard
              <ArrowRight className="ml-2 size-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-slate-700 bg-slate-800/60 text-slate-200 hover:text-white hover:bg-slate-700/60 font-medium px-8 rounded-lg"
              onClick={() => window.location.assign('/demo')}
            >
              Book a Demo
            </Button>
          </div>
        </section>

        {/* Dashboard Preview Image Placeholder */}
        <section className="max-w-6xl mx-auto mb-24 relative">
          <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4 md:p-8 shadow-2xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard value="$142,500" label="Revenue Attributed" color="green" />
              <StatCard value="42" label="Qualified Leads" color="blue" />
              <StatCard value="$128" label="Cost Per Lead" color="pink" />
              <StatCard value="4.9/5" label="Reputation Score" color="amber" />
            </div>
            <div className="relative rounded-xl overflow-hidden border border-slate-800/80 shadow-xl bg-[#090D16]">
              <img
                src="/product-frames/marketing.webp"
                alt="Rooflyzer Marketing Command Center — Multi-channel ROI, cost-per-lead, and campaign attribution"
                className="w-full h-auto block"
              />
            </div>
          </div>
        </section>

        {/* Key Features Grid */}
        <section className="max-w-6xl mx-auto mb-24">
          <div className="grid md:grid-cols-3 gap-6">
            <FeatureCard
              icon={Globe}
              title="Website Integration"
              description="Track every form fill and call from your website. Know exactly where your leads come from."
              features={['Source Attribution', 'Landing Page Tracking', 'Form Analytics']}
              color="blue"
            />
            <FeatureCard
              icon={Search}
              title="SEO Power Suite"
              description="Monitor your rankings and fix technical issues that are killing your visibility."
              features={['One-click WordPress Login', 'Tech SEO Cleanup', 'Page Speed Checks']}
              color="green"
            />
            <FeatureCard
              icon={Megaphone}
              title="Campaign ROI"
              description="Connect ad spend to actual revenue. Stop guessing which ads are working."
              features={['Google Ads Sync', 'Meta Ads Sync', 'Cost Per Acquisition']}
              color="pink"
            />
          </div>
        </section>

        {/* Deep Dive Sections */}
        <section className="max-w-6xl mx-auto space-y-24 mb-24">
          {/* Reputation */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <SectionBadge icon={MessageSquare} text="Reputation" color="amber" className="mb-4" />
              <h2 className="text-3xl font-bold text-white mb-4">Protect Your Brand</h2>
              <p className="text-white/90 text-lg mb-6">
                Your reputation is everything. We track every mention of your brand across the web
                and help you generate more 5-star reviews.
              </p>
              <ul className="space-y-4">
                {[
                  'AI Brand Mention Tracking',
                  'Review Monitoring & Replying',
                  'Automated Review Request Drips',
                  'Negative Feedback Interception',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/80">
                    <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <AlertCircle className="w-4 h-4 text-amber-400" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
              <div className="space-y-4">
                <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                  <div className="flex justify-between mb-2">
                    <span className="text-white font-bold">New Google Review</span>
                    <div className="flex text-amber-400">★★★★★</div>
                  </div>
                  <p className="text-white/90 text-sm">
                    "Best roofing company in town! They were fast, professional, and..."
                  </p>
                </div>
                <div className="bg-white/5 p-4 rounded-xl border border-white/10 opacity-70">
                  <div className="flex justify-between mb-2">
                    <span className="text-white font-bold">Facebook Mention</span>
                    <span className="text-slate-400 text-xs">Just now</span>
                  </div>
                  <p className="text-white/90 text-sm">
                    "Anyone know a good roofer? Thinking of calling [Your Company]..."
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Local SEO */}
          <div className="grid md:grid-cols-2 gap-12 items-center md:flex-row-reverse">
            <div className="order-2 md:order-1 rounded-2xl overflow-hidden border border-slate-800/80 shadow-xl bg-[#090D16]">
              <img
                src="/product-frames/reports.webp"
                alt="Rooflyzer Executive Analytics & Local Territory Attribution"
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
            <div className="order-1 md:order-2">
              <SectionBadge icon={MapPin} text="Local Dominance" color="indigo" className="mb-4" />
              <h2 className="text-3xl font-bold text-white mb-4">Own Your Neighborhood</h2>
              <p className="text-white/90 text-lg mb-6">
                Dominate the Google Map Pack in your service area. We automate the tedious parts of
                local SEO.
              </p>
              <ul className="space-y-4">
                {[
                  'Google Business Profile Sync',
                  'Citation Submission & Tracking',
                  'NAP Consistency Checks',
                  'Local Competitor Tracking',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/80">
                    <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center">
                      <BarChart3 className="w-4 h-4 text-indigo-400" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Product Screenshot */}
        <section className="mt-16 mb-8 max-w-6xl mx-auto px-4 sm:px-6">
          <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
            <img
              src="/product-frames/marketing.webp"
              alt="Rooflyzer — Email marketing campaigns and automation hub"
              className="w-full h-auto"
              loading="lazy"
            />
          </div>
        </section>

        {/* Final CTA */}
        <section className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">The Truth Is In The Numbers</h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            This dashboard is included for free because we believe you shouldn't have to pay extra
            just to know if your marketing works.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-black hover:bg-white/90 font-bold px-8"
              onClick={() => window.location.assign(getAuthLink('/register'))}
            >
              Start Tracking Today
              <ArrowRight className="ml-2 size-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/15 text-white hover:bg-white/5 hover:border-white/25 font-medium px-8"
              onClick={() => window.location.assign('/demo')}
            >
              Book a Demo
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};

export default MarketingDashboardPage;
